import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Table } from 'react-bootstrap';
import { Link, useLocation, useParams } from 'react-router-dom';
import { canAccessEngineeringModule } from '../../utils/roleConstants';
import { getStoredUser } from '../Tooling/tpmRequest';
import { authenticatedIdentity } from '../../_services/apiFetch';
import DatacoLoadingIndicator from '../../_components/DatacoLoadingIndicator';
import { machiningMaintenanceService as service } from '../../_services/machiningMaintenance.service';
import { AssetPmChecklistForm } from './AssetPmChecklistForm';
import { AssetPmRecordForm } from './AssetPmRecordDetail';
import { AssetPmRecordList } from './AssetPmRecordList';

function MaintenanceContent({ kind, title, slug, assetId, recordId, identity, initialView }) {
    const [query, setQuery] = useState({ page: 1, pageSize: 25, search: '' });
    const [search, setSearch] = useState('');
    const [list, setList] = useState(null);
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);
    const [error, setError] = useState(null);
    const [denied, setDenied] = useState(false);
    const [refresh, setRefresh] = useState(0);
    const [view, setView] = useState(initialView);
    const queryKey = JSON.stringify(query);

    useEffect(() => {
        let disposed = false;
        let pending = false;
        let blocked = false;
        const current = () => !disposed && identity === authenticatedIdentity();

        async function load(force = false) {
            if (!current() || pending || blocked) return;
            pending = true;
            setLoading(true);

            try {
                if (recordId) {
                    const result = await service.getRecord(kind, recordId);
                    if (current()) setDetail(result);
                } else if (assetId) {
                    const result = await service.getChecklist(kind, assetId);
                    if (current()) setDetail(result);
                } else {
                    const read =
                        view === 'records'
                            ? service.listRecords(kind, JSON.parse(queryKey), force)
                            : service.list(kind, JSON.parse(queryKey), force);
                    if (current()) setList(read.data || null);
                    const result = await read.revalidate;
                    if (current()) setList(result);
                }
                if (current()) setError(null);
            } catch (failure) {
                if (current()) {
                    setError(failure);
                    if (failure.status === 401 || failure.status === 403) {
                        blocked = true;
                        service.clear();
                        setDenied(true);
                        setList(null);
                        setDetail(null);
                    }
                }
            } finally {
                pending = false;
                if (current()) setLoading(false);
            }
        }

        function visibleRefresh() {
            if (!document.hidden && navigator.onLine && !assetId && !recordId) load(true);
        }

        load();
        const timer = setInterval(visibleRefresh, 60000);
        window.addEventListener('focus', visibleRefresh);
        window.addEventListener('online', visibleRefresh);
        document.addEventListener('visibilitychange', visibleRefresh);

        return () => {
            disposed = true;
            clearInterval(timer);
            window.removeEventListener('focus', visibleRefresh);
            window.removeEventListener('online', visibleRefresh);
            document.removeEventListener('visibilitychange', visibleRefresh);
        };
    }, [kind, assetId, recordId, identity, queryKey, refresh, view]);

    if (denied) return <Alert variant="danger">Engineering access is required.</Alert>;

    async function mutateRecord(operation, payload) {
        try {
            return await operation(kind, recordId, payload);
        } catch (failure) {
            if (failure.status === 401 || failure.status === 403) {
                service.clear();
                setDetail(null);
                setDenied(true);
            }

            throw failure;
        }
    }

    const base = `/tpm/${slug}/preventive-maintenance`;
    const Layout = recordId ? 'div' : Card;
    const LayoutBody = recordId ? 'div' : Card.Body;
    return (
        <Layout className={recordId ? 'container py-3' : 'm-3'}>
            <LayoutBody>
                {!recordId && <h1>{title} Preventive Maintenance</h1>}
                {!assetId && !recordId && (
                    <div className="d-flex gap-2 mb-3">
                        <Button
                            variant={view === 'checklists' ? 'primary' : 'outline-primary'}
                            onClick={() => {
                                if (view === 'checklists') return;

                                setView('checklists');
                                setList(null);
                                setQuery({ page: 1, pageSize: 25, search: '' });
                            }}
                        >
                            Checklists
                        </Button>
                        <Button
                            variant={view === 'records' ? 'primary' : 'outline-primary'}
                            onClick={() => {
                                if (view === 'records') return;

                                setView('records');
                                setList(null);
                                setQuery({ page: 1, pageSize: 25, search: '' });
                            }}
                        >
                            Maintenance records
                        </Button>
                    </div>
                )}
                {error && (
                    <Alert variant="danger">
                        {error.message}{' '}
                        <Button onClick={() => setRefresh((value) => value + 1)}>Retry</Button>
                    </Alert>
                )}
                {assetId && !detail && !error && (
                    <DatacoLoadingIndicator label="Loading checklist…" />
                )}
                {recordId && !detail && !error && <p role="status">Loading maintenance record…</p>}
                {recordId ? (
                    detail && (
                        <AssetPmRecordForm
                            kind={kind}
                            title={title}
                            key={recordId}
                            detail={detail}
                            slug={slug}
                            onClose={(payload) => mutateRecord(service.closeRecord, payload)}
                            onReopen={(payload) => mutateRecord(service.reopenRecord, payload)}
                        />
                    )
                ) : assetId ? (
                    detail && (
                        <AssetPmChecklistForm
                            kind={kind}
                            key={assetId}
                            detail={detail}
                            onSave={(payload) => service.saveChecklist(kind, assetId, payload)}
                        />
                    )
                ) : (
                    <>
                        {view === 'checklists' && (
                            <Form
                                onSubmit={(event) => {
                                    event.preventDefault();
                                    setQuery((previous) => ({ ...previous, page: 1, search }));
                                }}
                                className="d-flex gap-2 mb-3"
                            >
                                <Form.Control
                                    aria-label="Search assets"
                                    value={search}
                                    onChange={(event) => setSearch(event.target.value)}
                                />
                                <Button type="submit">Search</Button>
                            </Form>
                        )}
                        {view === 'records' ? (
                            <AssetPmRecordList
                                slug={slug}
                                list={list}
                                query={query}
                                onQuery={setQuery}
                                loading={loading}
                            />
                        ) : (
                            !list && !error && (
                                <DatacoLoadingIndicator label="Loading checklists…" />
                            )
                        )}
                        {view === 'checklists' && list && (
                            <>
                                <Table responsive striped>
                                    <thead>
                                        <tr>
                                            <th>Asset</th>
                                            <th>Checklist</th>
                                            <th>Start Date</th>
                                            <th>Next calendar dates</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {list.items.map((item) => (
                                            <tr key={item.asset.id}>
                                                <td>
                                                    {item.asset.name ||
                                                        item.asset.machine ||
                                                        item.asset.testerNo ||
                                                        item.asset.jigFixNo}
                                                </td>
                                                <td>
                                                    {item.checklist?.isActive
                                                        ? 'Active'
                                                        : item.checklist
                                                          ? 'Draft'
                                                          : 'Not configured'}
                                                </td>
                                                <td>
                                                    {item.checklist?.maintenanceStartDate || '—'}
                                                </td>
                                                <td>
                                                    {!item.schedules.some((schedule) => schedule.isEnabled) &&
                                                        'No frequencies selected'}
                                                    {item.schedules
                                                        .filter((schedule) => schedule.isEnabled)
                                                        .map((schedule) => (
                                                        <div key={schedule.frequency}>
                                                            {schedule.frequency}:{' '}
                                                            {schedule.nextDueDate || '—'}
                                                        </div>
                                                        ))}
                                                </td>
                                                <td>
                                                    <Link
                                                        to={
                                                            base +
                                                            '/checklists/' +
                                                            encodeURIComponent(item.asset.id)
                                                        }
                                                    >
                                                        Edit checklist
                                                    </Link>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                                <p>
                                    {list.total} assets · Page {list.page}
                                </p>
                                <Button
                                    disabled={query.page === 1}
                                    onClick={() =>
                                        setQuery((previous) => ({
                                            ...previous,
                                            page: previous.page - 1,
                                        }))
                                    }
                                    className="me-2"
                                >
                                    Previous
                                </Button>
                                <Button
                                    disabled={query.page * query.pageSize >= list.total}
                                    onClick={() =>
                                        setQuery((previous) => ({
                                            ...previous,
                                            page: previous.page + 1,
                                        }))
                                    }
                                >
                                    Next
                                </Button>
                            </>
                        )}
                    </>
                )}
                {assetId && (
                    <Link className="d-block mt-3" to={base}>
                        Back to preventive maintenance
                    </Link>
                )}
            </LayoutBody>
        </Layout>
    );
}

export function AssetPreventiveMaintenance(props) {
    const { assetId, recordId } = useParams();
    const location = useLocation();
    const initialView =
        new URLSearchParams(location.search).get('view') === 'records' ? 'records' : 'checklists';
    const [identity, setIdentity] = useState(authenticatedIdentity);

    useEffect(() => {
        const update = () => setIdentity(authenticatedIdentity());
        const timer = setInterval(update, 1000);
        window.addEventListener('storage', update);
        window.addEventListener('focus', update);

        return () => {
            clearInterval(timer);
            window.removeEventListener('storage', update);
            window.removeEventListener('focus', update);
        };
    }, []);

    if (!canAccessEngineeringModule(getStoredUser()))
        return <Alert variant="danger">Engineering access is required.</Alert>;

    return (
        <MaintenanceContent
            key={identity + props.kind + (assetId || '') + (recordId || '')}
            {...props}
            identity={identity}
            assetId={assetId}
            recordId={recordId}
            initialView={initialView}
        />
    );
}
